import type { ActionDescriptor, ActionEffect, ActionSource, ActionStatus, TestRef } from './types'

/** Окна-псевдонимы общих записей: оболочка и общие элементы (кнопки ui-kit и общих компонентов). */
export const SHELL_WINDOW = 'shell'
export const COMMON_WINDOW = 'common'

/** Строка «как проверено» в карточке подсказки: что за доказательство и где оно. */
export interface ActionEvidenceLine {
  kind: 'unit' | 'server' | 'stand' | 'live'
  /** Имя теста или дата живой проверки. */
  title: string
  /** Файл теста (без каталогов) или запись протокола. */
  where: string
}

/**
 * Карточка подсказки — то, что показывает ui-kit. Та же форма объявлена в
 * ui-kit (`ActionInfoCard`): пакеты нижнего слоя друг друга не импортируют,
 * совместимость проверяет компилятор в composition приложения.
 */
export interface ActionCard {
  id: string
  label: string
  summary: string
  effect: ActionEffect
  effectText: string | null
  access: string | null
  disabledWhen: string | null
  gestureOf: string | null
  status: ActionStatus
  statusReason: string | null
  evidence: readonly ActionEvidenceLine[]
}

export interface ActionRegistry {
  readonly all: readonly ActionDescriptor[]
  readonly get: (id: string) => ActionDescriptor | undefined
  /** Карточка подсказки; `undefined` — записи нет (ⓘ не рисуется, кнопка работает как раньше). Можно передавать как значение. */
  readonly describe: (id: string) => ActionCard | undefined
}

const ID = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)+$/

/** E2E в браузере на стенде — сам по себе «тест + стенд»; ServerGate — только вместе с юнит-тестом кнопки. */
function isBrowserE2e(ref: TestRef): boolean {
  return ref.file.endsWith('.spec.ts')
}

/**
 * Статус по доказательствам — те же правила, что в аудите UI-ACTIONS:
 * живая запись — «проверено вживую»; E2E на стенде или ServerGate вместе с
 * тестом кнопки — «тест + стенд»; любой тест — «только тест»; ничего —
 * «не проверено». Руками — только «заглушка» и «назначение неясно».
 */
export function actionStatus(descriptor: ActionDescriptor): ActionStatus {
  if (descriptor.status !== undefined) return descriptor.status
  const { unit = [], server = [], stand = [], live = [] } = descriptor.evidence
  if (live.length > 0) return 'live'
  if (stand.some(isBrowserE2e) || (stand.length > 0 && unit.length > 0)) return 'stand-and-test'
  if (unit.length + server.length + stand.length > 0) return 'test-only'
  return 'unverified'
}

const baseName = (file: string) => file.slice(file.lastIndexOf('/') + 1)

function evidenceLines(descriptor: ActionDescriptor): ActionEvidenceLine[] {
  const { unit = [], server = [], stand = [], live = [] } = descriptor.evidence
  const tests = (kind: ActionEvidenceLine['kind'], refs: readonly TestRef[]) =>
    refs.map((ref) => ({ kind, title: ref.name, where: baseName(ref.file) }))
  return [
    ...live.map((ref) => ({ kind: 'live' as const, title: ref.date, where: ref.record })),
    ...tests('stand', stand),
    ...tests('unit', unit),
    ...tests('server', server),
  ]
}

function cardOf(descriptor: ActionDescriptor): ActionCard {
  return {
    id: descriptor.id,
    label: descriptor.label,
    summary: descriptor.summary,
    effect: descriptor.effect,
    effectText: descriptor.effectText ?? null,
    access: descriptor.access ?? null,
    disabledWhen: descriptor.disabledWhen ?? null,
    gestureOf: descriptor.gestureOf ?? null,
    status: actionStatus(descriptor),
    statusReason: descriptor.statusReason ?? null,
    evidence: evidenceLines(descriptor),
  }
}

/** Имена видов ресурса: в общих записях оболочки их быть не может (ADR-18). */
const KIND_NAMES = /\b(?:onec|aqsi)\b|1[СC]|AQSI/i

function textsOf(descriptor: ActionDescriptor): string[] {
  return [
    descriptor.label,
    descriptor.summary,
    descriptor.effectText,
    descriptor.access,
    descriptor.disabledWhen,
    descriptor.gestureOf,
    descriptor.statusReason,
  ].filter((text): text is string => text !== undefined)
}

function validate(sources: readonly ActionSource[], windows: ReadonlySet<string>): ActionDescriptor[] {
  const ids = new Set<string>()
  const all: ActionDescriptor[] = []
  for (const source of sources) {
    const prefixes = new Set([source.id, ...(source.extraPrefixes ?? [])])
    for (const descriptor of source.actions ?? []) {
      const where = `Действие «${descriptor.id}»`
      if (!ID.test(descriptor.id)) throw new Error(`${where}: имя — <модуль>.<окно>.<действие> латиницей`)
      const prefix = descriptor.id.slice(0, descriptor.id.indexOf('.'))
      if (!prefixes.has(prefix)) {
        throw new Error(`${where}: модуль «${source.id}» объявляет только ${[...prefixes].map((name) => `${name}.*`).join(', ')}`)
      }
      if (ids.has(descriptor.id)) throw new Error(`${where} объявлено дважды`)
      ids.add(descriptor.id)
      if (descriptor.windows.length === 0) throw new Error(`${where}: не названо ни одного окна`)
      for (const window of descriptor.windows) {
        if (window !== SHELL_WINDOW && window !== COMMON_WINDOW && !windows.has(window)) {
          throw new Error(`${where}: окна «${window}» нет в реестре окон`)
        }
      }
      if (descriptor.label.trim() === '' || descriptor.summary.trim() === '') {
        throw new Error(`${where}: нужны подпись и описание «что делает»`)
      }
      if (descriptor.status !== undefined && (descriptor.statusReason ?? '').trim() === '') {
        throw new Error(`${where}: статус «${descriptor.status}» ставится только с причиной`)
      }
      if (descriptor.status === undefined && descriptor.statusReason !== undefined) {
        throw new Error(`${where}: причина статуса без статуса — статус вычисляется из доказательств`)
      }
      const { unit = [], server = [], stand = [] } = descriptor.evidence
      for (const ref of [...unit, ...server, ...stand]) {
        if (ref.file.trim() === '' || ref.file.startsWith('/') || ref.name.trim() === '') {
          throw new Error(`${where}: тест — путь от корня репозитория и точное имя`)
        }
      }
      if (prefix === SHELL_WINDOW || prefix === COMMON_WINDOW) {
        // Общие записи (оболочка, общие элементы) не знают видов ресурса (ADR-18).
        const named = textsOf(descriptor).find((text) => KIND_NAMES.test(text))
        if (named !== undefined) throw new Error(`${where}: общая запись называет вид ресурса — «${named}»`)
      }
      all.push(descriptor)
    }
  }
  return all
}

/**
 * Реестр действий приложения: один, собирается composition из модулей.
 * Ошибка здесь роняет сборку приложения и тест composition, как у реестра окон.
 */
export function createActionRegistry(sources: readonly ActionSource[], windowKinds: readonly string[]): ActionRegistry {
  const all = validate(sources, new Set(windowKinds))
  const byId = new Map(all.map((descriptor) => [descriptor.id, descriptor]))
  const cards = new Map<string, ActionCard>()
  return {
    all,
    get: (id) => byId.get(id),
    describe: (id) => {
      const cached = cards.get(id)
      if (cached !== undefined) return cached
      const descriptor = byId.get(id)
      if (descriptor === undefined) return undefined
      const card = cardOf(descriptor)
      cards.set(id, card)
      return card
    },
  }
}
